import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiFetch } from './api';
import type {
  AiSummary,
  Child,
  ConfidenceLevel,
  FreeTimeSlot,
  ProposalsResponse,
  SessionFormat,
  StudySession,
  TimetableEntry,
} from '../types/api';

export function useTimetable(childId: string) {
  return useQuery({
    queryKey: ['child', childId, 'timetable'],
    queryFn: () =>
      apiFetch<{ child_id: string; entries: TimetableEntry[]; updated_at: string }>(
        `/children/${childId}/timetable`,
      ),
    retry: (count, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return count < 2;
    },
  });
}

export function usePutTimetable(childId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entries: TimetableEntry[]) =>
      apiFetch<{ child_id: string; entries_count: number }>(`/children/${childId}/timetable`, {
        method: 'PUT',
        body: JSON.stringify({ entries }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['child', childId, 'timetable'] }),
  });
}

export function useFreeTime(childId: string) {
  return useQuery({
    queryKey: ['child', childId, 'free-time'],
    queryFn: () =>
      apiFetch<{ child_id: string; slots: FreeTimeSlot[]; updated_at: string }>(
        `/children/${childId}/free-time`,
      ),
    retry: (count, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return count < 2;
    },
  });
}

export function usePutFreeTime(childId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slots: FreeTimeSlot[]) =>
      apiFetch<{ child_id: string; slots_count: number }>(`/children/${childId}/free-time`, {
        method: 'PUT',
        body: JSON.stringify({ slots }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['child', childId, 'free-time'] }),
  });
}

export function useConfidence(childId: string) {
  return useQuery({
    queryKey: ['child', childId, 'confidence'],
    queryFn: () =>
      apiFetch<{ child_id: string; subjects: Record<string, ConfidenceLevel> }>(
        `/children/${childId}/confidence`,
      ),
    retry: (count, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return count < 2;
    },
  });
}

export function usePutConfidence(childId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (subjects: Record<string, ConfidenceLevel>) =>
      apiFetch<{ child_id: string; subjects: Record<string, ConfidenceLevel> }>(
        `/children/${childId}/confidence`,
        { method: 'PUT', body: JSON.stringify({ subjects }) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['child', childId, 'confidence'] });
      qc.invalidateQueries({ queryKey: ['child', childId, 'proposals'] });
    },
  });
}

export function useAiSummary(childId: string) {
  return useQuery({
    queryKey: ['child', childId, 'ai-summary'],
    queryFn: () => apiFetch<AiSummary>(`/children/${childId}/ai-summary`),
    staleTime: 5 * 60 * 1000,
  });
}

export function useProposals(childId: string) {
  return useQuery({
    queryKey: ['child', childId, 'proposals'],
    queryFn: () => apiFetch<ProposalsResponse>(`/children/${childId}/proposals`),
    staleTime: 10 * 60 * 1000,
    enabled: !!childId,
    retry: (count, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return count < 2;
    },
  });
}

export function useSessions(childId: string) {
  return useQuery({
    queryKey: ['child', childId, 'sessions'],
    queryFn: () =>
      apiFetch<{ sessions: StudySession[]; count: number }>(`/children/${childId}/sessions`).then(
        (r) => r.sessions,
      ),
  });
}

export function useJoinCode(childId: string) {
  return useQuery({
    queryKey: ['child', childId, 'join-code'],
    queryFn: () =>
      apiFetch<{ join_code: string; join_code_expires_at: string }>(
        `/children/${childId}/join-code`,
      ),
    retry: (count, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return count < 2;
    },
  });
}

export function useRegenerateJoinCode(childId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ join_code: string; join_code_expires_at: string }>(
        `/children/${childId}/regenerate-code`,
        { method: 'POST' },
      ),
    onSuccess: (data) => {
      qc.setQueryData(['child', childId, 'join-code'], data);
    },
  });
}

export function useOcrTimetable(childId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { image_base64: string; media_type: string }) =>
      apiFetch<{ entries: TimetableEntry[]; rejected_count: number }>(
        `/children/${childId}/timetable/ocr`,
        { method: 'POST', body: JSON.stringify(payload) },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['child', childId, 'timetable'] }),
  });
}

export function useLogSession(childId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      subject: string;
      topic: string;
      format: SessionFormat;
      duration_minutes: number;
      note?: string;
    }) =>
      apiFetch<{ session_id: string; streak_count: number }>(`/children/${childId}/sessions`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['child', childId, 'sessions'] });
      qc.invalidateQueries({ queryKey: ['parent', 'children'] });
    },
  });
}

export function useChildProfile(childId: string) {
  return useQuery({
    queryKey: ['child', childId, 'profile'],
    queryFn: () => apiFetch<Child>(`/children/${childId}/profile`),
    enabled: !!childId,
  });
}

export function useUpdateChildProfile(childId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      country?: string;
      education_authority?: string; // deprecated — backend still accepts for compat
      education_authorities?: string[];
      school_year?: string;
    }) =>
      apiFetch<{ child_id: string }>(`/children/${childId}/profile`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['child', childId, 'profile'] });
      qc.invalidateQueries({ queryKey: ['parent', 'children'] });
    },
  });
}

/** New-academic-year reset (US-PRF-01/US-PRF-02) — a heavier, more destructive
 * operation than useUpdateChildProfile above: the backend hard-deletes the
 * timetable, free time, commitments, and cached proposals, so every query
 * that could contain any of that stale data must be invalidated too. */
export function useUpdateAcademicContext(childId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      country_code: string;
      study_level: string;
      education_authorities?: string[];
    }) =>
      apiFetch<{ status: string; timetable_required: boolean }>(
        `/children/${childId}/academic-context`,
        { method: 'PUT', body: JSON.stringify(body) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['child', childId, 'profile'] });
      qc.invalidateQueries({ queryKey: ['child', childId, 'timetable'] });
      qc.invalidateQueries({ queryKey: ['child', childId, 'free-time'] });
      qc.invalidateQueries({ queryKey: ['child', childId, 'proposals'] });
      qc.invalidateQueries({ queryKey: ['parent', 'children'] });
    },
  });
}
